/* @jsxImportSource @emotion/react */

import React from 'react'
import { css } from '@emotion/react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ImgSanity } from '@slite/components'

import { useRoutes } from '../routes'
import { MEDIUM_BREAKPOINT, SMALL_BREAKPOINT } from '../utils/breakpoints'
import { Button } from './Button/Button'
import { Section } from './Section'
import { WebsiteText } from './WebsiteText'

// Sanity logo URLs per competitor — used by the migration teaser block that
// renders only when `competitorName` is set.
const COMPETITOR_IMAGE_URLS: Record<string, string> = {
  Notion:
    'https://cdn.sanity.io/images/dnsgqii5/production/c8da97d64333003a51946ae1f21324e22899ca8e-1396x723.png',
  Confluence:
    'https://cdn.sanity.io/images/dnsgqii5/production/881a35c3714e66037c6e2cf43360f90f809487f8-1396x723.png',
  Slab: 'https://cdn.sanity.io/images/dnsgqii5/production/526cbb48397eba3b30078e76d0419bf528dae6da-1767x1053.jpg',
}
const FALLBACK_COMPETITOR_IMAGE = COMPETITOR_IMAGE_URLS.Notion

export function SectionLastCTA({
  title,
  titleMaxWidth,
  subtitle,
  subtitleMaxWidth,
  background = 'shade-100',
  buttons,
  buttonsMicrocopy,
  competitorName,
  containerVerticalPadding = 120,
}: {
  title?: React.ReactNode
  titleMaxWidth?: number
  subtitle?: React.ReactNode
  background?: string
  subtitleMaxWidth?: number
  // When provided, prepends a migration teaser block (used on /compare pages).
  // Carries its own copy + an illustration of the competitor's product.
  competitorName?: string
  buttons?: {
    label: React.ReactNode
    href: string
    variant?: 'primary' | 'primary-brand' | 'secondary' | 'ghost'
    size?: 'small' | 'default' | 'huge'
    onClick?: () => void
  }[]
  // Small reassurance line rendered under the buttons row.
  buttonsMicrocopy?: React.ReactNode
  containerVerticalPadding?: number
}) {
  const intl = useIntl()
  const routes = useRoutes()
  return (
    <Section
      background={background}
      containerTopPadding={competitorName ? 0 : undefined}
      containerVerticalPadding={containerVerticalPadding}
    >
      {!!competitorName && (
        <div className="grid-12 grid-gap-40 grid-align-center margin-bottom-60 padding-bottom-60">
          <div className="grid-span-6 grid-span-10-small grid-offset-1-small flexbox-column flexbox-align-center-small text-align-center-small">
            <h4 className="font-family-title font-weight-medium font-size-4 margin-bottom-4">
              <FormattedMessage
                defaultMessage="Tempting, but nobody has time to migrate all the docs..."
                id="section.lastcta.title"
              />
            </h4>
            <WebsiteText variant="body-lg" className="margin-bottom-12">
              <FormattedMessage
                id="section.lastcta.desc"
                defaultMessage="We get it, switching tools can be painful. But migrating your docs from {competitorName} to Slite with our self-serve import tools can take minutes, not days.{br}{br}For teams over 50, our experts handle the migration with you."
                values={{
                  competitorName,
                  br: <br />,
                }}
              />
            </WebsiteText>
            <Button
              variant="secondary"
              size="default"
              href={routes.bookDemo()}
              className="margin-top-12"
            >
              <FormattedMessage
                id="section.lastcta.bookdemo"
                defaultMessage="Book your onboarding"
              />
            </Button>
          </div>

          <ImgSanity
            className="grid-span-6 grid-span-12-small radius-larger"
            alt={intl.formatMessage(
              {
                id: 'section.lastcta.imagealt',
                defaultMessage: 'Import from {competitorName}',
              },
              { competitorName }
            )}
            src={
              COMPETITOR_IMAGE_URLS[competitorName] ?? FALLBACK_COMPETITOR_IMAGE
            }
          />
        </div>
      )}

      <WebsiteText
        variant="title"
        className="text-align-center"
        css={css`
          max-width: ${titleMaxWidth ?? 850}px;
          margin: 12px auto 0 auto;
        `}
      >
        {title ?? (
          <FormattedMessage
            id="section.cta.text"
            defaultMessage="The self-maintaining knowledge base your team and agents can trust"
          />
        )}
      </WebsiteText>

      {subtitle && (
        <WebsiteText
          variant="body"
          className="text-align-center font-family-title line-height-larger"
          css={css`
            max-width: ${subtitleMaxWidth ?? 800}px;
            margin: 12px auto 0 auto;

            @media screen and (min-width: ${MEDIUM_BREAKPOINT}px) {
              max-width: ${subtitleMaxWidth ? `${subtitleMaxWidth}px` : '55%'};
            }
          `}
        >
          {subtitle}
        </WebsiteText>
      )}
      <div
        className="margin-top-24 padding-top-4 flexbox flexbox-centered flexbox-wrap"
        css={css`
          gap: 12px;
          /* Stack CTAs vertically full-width on mobile so they're tappable
             at thumb size. */
          @media (max-width: ${SMALL_BREAKPOINT - 1}px) {
            flex-direction: column;
            & > * {
              width: 100%;
            }
          }
        `}
      >
        {buttons?.length ? (
          buttons.map((b) => (
            <Button
              key={b.href}
              href={b.href}
              variant={b.variant ?? 'primary'}
              size={b.size ?? 'default'}
              onClick={b.onClick}
              className="text-align-center"
            >
              {b.label}
            </Button>
          ))
        ) : (
          <>
            <Button
              variant="primary-brand"
              size="huge"
              href={routes.bookDemo()}
            >
              <FormattedMessage
                id="section.lastchance.bookdemo.button"
                defaultMessage="Book demo"
              />
            </Button>
            <Button
              variant="ghost"
              size="default"
              href={routes.pricing()}
              withChevron
            >
              <FormattedMessage
                id="section.lastchance.pricing"
                defaultMessage="See pricing"
              />
            </Button>
          </>
        )}
      </div>
      {buttonsMicrocopy && (
        <WebsiteText
          variant="body"
          className="text-align-center margin-top-12"
          css={css`
            && {
              font-size: 13px;
              opacity: 0.7;
            }
          `}
        >
          {buttonsMicrocopy}
        </WebsiteText>
      )}
    </Section>
  )
}
